import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import {
  MODAL_ANIMATION_DURATION_MS,
  MODAL_CONFIG,
  MODAL_CONTENT_COMPONENT,
  MODAL_REF,
  ModalRef,
} from '@services/components/ModalService/modal.ref';
import { ModalCloseReason, ResolvedModalConfig } from '@services/components/ModalService/modal.types';

@Component({
  selector: 'app-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.css',
  host: {
    class: 'modal-host',
    role: 'presentation',
  },
})
export class ModalComponent implements AfterViewInit, OnDestroy {
  private readonly modalRef = inject<ModalRef<unknown>>(MODAL_REF);
  protected readonly config = inject<ResolvedModalConfig>(MODAL_CONFIG);
  protected readonly contentComponentType = inject<Type<unknown> | undefined>(MODAL_CONTENT_COMPONENT, { optional: true });
  private readonly focusTrapFactory = inject(FocusTrapFactory);
  private readonly document = inject(DOCUMENT);

  @ViewChild('contentHost', { read: ViewContainerRef })
  private readonly contentHost!: ViewContainerRef;

  @ViewChild('dialogPanel')
  private readonly dialogPanel!: ElementRef<HTMLElement>;

  protected readonly isOpen = signal(false);
  protected readonly panelClasses = computed(
    () =>
      `modal-panel modal-panel--${this.config.size}${this.isOpen() ? ' is-open' : ''}`,
  );

  private focusTrap?: FocusTrap;
  private escCleanup?: () => void;
  private animationTimeout?: ReturnType<typeof setTimeout>;

  public ngAfterViewInit(): void {
    this.renderContentComponent();
    this.setupFocusTrap();
    this.setupEscListener();
    requestAnimationFrame(() => this.isOpen.set(true));
  }

  public ngOnDestroy(): void {
    this.escCleanup?.();
    this.focusTrap?.destroy();
    if (this.animationTimeout !== undefined) {
      clearTimeout(this.animationTimeout);
    }
  }

  protected requestClose(reason: ModalCloseReason): void {
    if (!this.isOpen()) {
      return;
    }

    this.isOpen.set(false);

    this.animationTimeout = setTimeout(() => {
      this.modalRef.close({ reason });
    }, MODAL_ANIMATION_DURATION_MS);
  }

  protected onBackdropClick(): void {
    if (this.config.closeOnBackdrop) {
      this.requestClose('backdrop');
    }
  }

  private renderContentComponent(): void {
    if (this.contentComponentType) {
      this.contentHost.createComponent(this.contentComponentType);
    }
  }

  private setupFocusTrap(): void {
    this.focusTrap = this.focusTrapFactory.create(this.dialogPanel.nativeElement);
    this.focusTrap.focusInitialElementWhenReady().catch(() => {
      // Fallback: focus the panel container so keyboard navigation still works.
      this.dialogPanel.nativeElement.focus();
    });
  }

  private setupEscListener(): void {
    if (!this.config.closeOnEsc) {
      return;
    }

    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.requestClose('escape');
      }
    };

    this.document.addEventListener('keydown', handler);
    this.escCleanup = (): void => this.document.removeEventListener('keydown', handler);
  }
}
